import { onBeforeUnmount, ref, watch, type Ref } from 'vue'
import { resolveInfoHtmlHref, type InfoHtmlDocRef } from '@/utils/infoHtmlFrame'

const HOST = 'info-html-host'

function readChecks(docId: string) {
  if (!docId) return {}
  try {
    const parsed = JSON.parse(localStorage.getItem(`info-html-checks:${docId}`) || '{}') as Record<string, unknown>
    const map: Record<string, string> = {}
    Object.entries(parsed).forEach(([key, value]) => {
      if (value === 'ready' || value === 'review' || value === 'fix') map[key] = value
    })
    return map
  } catch {
    return {}
  }
}

function writeChecks(docId: string, value: unknown) {
  if (!docId || !value || typeof value !== 'object') return
  const map: Record<string, string> = {}
  Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
    if (key.length > 80) return
    if (item === 'ready' || item === 'review' || item === 'fix') map[key] = item
  })
  try {
    localStorage.setItem(`info-html-checks:${docId}`, JSON.stringify(map))
  } catch {
    /* 저장 공간이 없으면 이번 화면에서만 유지한다 */
  }
}

export function useInfoHtmlFrame<T extends InfoHtmlDocRef>(options: {
  frameEl: Ref<HTMLIFrameElement | null>
  docs: Ref<T[]>
  currentId: Ref<string>
  focusId: Ref<string>
  onOpenDoc: (id: string, hash: string) => void
  onMissing?: (message: string) => void
}) {
  const htmlFull = ref(false)

  function post(message: Record<string, unknown>) {
    const frame = options.frameEl.value?.contentWindow
    if (!frame) return
    frame.postMessage({ source: HOST, ...message }, '*')
  }

  function focusInFrame(id: string) {
    if (!id) return
    post({ kind: 'focus', id })
  }

  function fitFrame(height: number) {
    const frame = options.frameEl.value
    if (!frame || height < 1) return
    const filling = htmlFull.value || document.fullscreenElement === frame
    if (filling) {
      frame.style.height = '100%'
      return
    }
    const next = Math.min(200000, Math.max(640, Math.ceil(height)))
    const current = frame.getBoundingClientRect().height
    if (current >= next - 4 && current <= next + 32) return
    frame.style.height = `${next}px`
  }

  function lockScroll(on: boolean) {
    document.body.classList.toggle('is-info-html-full', on)
  }

  async function exitHtmlFull() {
    htmlFull.value = false
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen()
      } catch {
        /* 이미 닫힌 전체화면 */
      }
    }
  }

  async function toggleHtmlFull() {
    if (document.fullscreenElement || htmlFull.value) {
      await exitHtmlFull()
      return
    }
    const frame = options.frameEl.value
    if (!frame) return
    try {
      await frame.requestFullscreen()
    } catch {
      htmlFull.value = true
    }
  }

  function onMessage(event: MessageEvent) {
    if (event.source !== options.frameEl.value?.contentWindow) return
    const data = event.data as { source?: string; kind?: string; href?: string; map?: unknown; id?: string; height?: number } | null
    if (!data || data.source !== 'info-html') return
    if (data.kind === 'height') {
      fitFrame(Number(data.height) || 0)
      return
    }
    if (data.kind === 'ready') {
      post({ kind: 'checks', map: readChecks(options.currentId.value) })
      if (options.focusId.value) {
        focusInFrame(options.focusId.value)
        options.focusId.value = ''
      }
      return
    }
    if (data.kind === 'checks') {
      writeChecks(options.currentId.value, data.map)
      return
    }
    if (data.kind === 'fullscreen') {
      void toggleHtmlFull()
      return
    }
    if (data.kind !== 'open' || !data.href) return
    const target = resolveInfoHtmlHref(data.href, options.docs.value, options.currentId.value)
    if (!target) return
    if (target.kind === 'external') {
      if (/^https?:/i.test(target.href)) window.open(target.href, '_blank', 'noopener,noreferrer')
      else window.location.href = target.href
      return
    }
    if (target.kind === 'missing') {
      options.onMissing?.(target.message)
      return
    }
    options.onMissing?.('')
    if (target.id === options.currentId.value) {
      focusInFrame(target.hash)
      return
    }
    options.focusId.value = target.hash
    options.onOpenDoc(target.id, target.hash)
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape' || !htmlFull.value) return
    event.preventDefault()
    event.stopPropagation()
    htmlFull.value = false
  }

  function onFullscreenChange() {
    lockScroll(htmlFull.value || document.fullscreenElement === options.frameEl.value)
  }

  watch(htmlFull, (on) => {
    lockScroll(on || document.fullscreenElement === options.frameEl.value)
    const frame = options.frameEl.value
    if (!frame) return
    if (on) frame.style.height = '100%'
    else {
      frame.style.height = ''
      post({ kind: 'measure' })
    }
  })

  window.addEventListener('message', onMessage)
  window.addEventListener('keydown', onKeydown, true)
  document.addEventListener('fullscreenchange', onFullscreenChange)

  onBeforeUnmount(() => {
    window.removeEventListener('message', onMessage)
    window.removeEventListener('keydown', onKeydown, true)
    document.removeEventListener('fullscreenchange', onFullscreenChange)
    document.body.classList.remove('is-info-html-full')
    if (document.fullscreenElement === options.frameEl.value) {
      void document.exitFullscreen().catch(() => undefined)
    }
  })

  return { htmlFull, toggleHtmlFull, exitHtmlFull, focusInFrame }
}
